import { useEffect, useState } from "react";
import UsersTable from "./UsersTable.tsx";
import {
  useGetSimpleSecurityUsersQuery,
  useGetSimpleSecurityDefinitionQuery,
  useUpdateSimpleSecurityDefinitionMutation,
  useUpdateSimpleSecurityUserMutation,
  useAddSimpleSecurityUserMutation,
  useDeleteSimpleSecurityUserMutation,
} from "../../../services/ApiSlice.ts";
import {
  SimpleSecurityDefinitionAdminDto,
  SimpleSecurityUserAdminDto,
} from "../../../services/ApiTypes";
import { useParams } from "react-router";
import AccessDefinitionModal from "./AccessDefinitionModal.tsx";
import UserEditModal from "./UserEditModal.tsx";
import CustomSpinner from "../../core/spinner/CustomSpinner.tsx";
import { Button, Modal } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import "./Users.css";
import { useNavigate } from "react-router-dom";

const UsersComponent = () => {
  const { configurationId = "" } = useParams<{ configurationId: string }>();

  const navigate = useNavigate();

  const [isUserEditModalVisible, setIsUserEditModalVisible] =
    useState<boolean>(false);
  const [isDefinitionEditModalVisible, setIsDefinitionEditModalVisible] =
    useState<boolean>(false);
  const [selectedUser, setSelectedUser] =
    useState<SimpleSecurityUserAdminDto>();

  const { data: simpleSecurityUsersData, isLoading: isUsersLoading } =
    useGetSimpleSecurityUsersQuery(configurationId);
  const {
    data: simpleSecurityDefinitionData,
    isLoading: isSimpleSecurityDefinitionLoading,
  } = useGetSimpleSecurityDefinitionQuery(configurationId);
  const [updateSimpleSecurityDefinition] =
    useUpdateSimpleSecurityDefinitionMutation();
  const [addSimpleSecurityUser] = useAddSimpleSecurityUserMutation();
  const [updateSimpleSecurityUser] = useUpdateSimpleSecurityUserMutation();
  const [deleteSimpleSecurityUser] = useDeleteSimpleSecurityUserMutation();

  const [simpleSecurityDefinition, setSimpleSecurityDefinition] =
    useState<SimpleSecurityDefinitionAdminDto>({
      id: "",
      name: "",
      surname: "",
      email: "",
      phoneNumber: "",
      additionalOne: "",
      additionalTwo: "",
    });

  useEffect(() => {
    if (simpleSecurityDefinitionData) {
      setSimpleSecurityDefinition(simpleSecurityDefinitionData);
    }
  }, [simpleSecurityDefinitionData]);

  const handleRowClick = (user: SimpleSecurityUserAdminDto, index: number) => {
    setSelectedUser(user);
    setIsUserEditModalVisible(true);
  };

  const handleAddUserClick = () => {
    setSelectedUser(undefined);
    setIsUserEditModalVisible(true);
  };

  const handleEditDefinitionClick = () => {
    setIsDefinitionEditModalVisible(true);
  };

  const handleUserEditModalClose = () => {
    setSelectedUser(undefined);
    setIsUserEditModalVisible(false);
  };

  const handleDefinitionEditModalClose = () => {
    setIsDefinitionEditModalVisible(false);
  };

  const handleSimpleSecurityDefinitionUpdate = async (
    updatedSimpleSecurityDefinition: SimpleSecurityDefinitionAdminDto
  ) => {
    await updateSimpleSecurityDefinition({
      configurationId,
      simpleSecurityDefinition: updatedSimpleSecurityDefinition,
    }).unwrap()
    .then(() => {
      handleUserEditModalClose();
    })
    .catch(() => {
      Modal.error({
        title: "Update Failed",
        content: "An error occurred while updating the access definition.",
      });
    });;
    handleDefinitionEditModalClose();
  };

  const handleUserUpdate = async (updatedUser: SimpleSecurityUserAdminDto) => {
    if (updatedUser.id !== null) {
      await updateSimpleSecurityUser({
        configurationId,
        simpleSecurityUser: updatedUser,
      }).unwrap()
      .then(() => {
        handleUserEditModalClose();
      })
      .catch(() => {
        Modal.error({
          title: "Update Failed",
          content: "An error occurred while adding the user.",
        });
      });
    } else {
      addSimpleSecurityUser({
        configurationId,
        simpleSecurityUser: updatedUser,
      }).unwrap()
        .then(() => {
          handleUserEditModalClose();
        })
        .catch(() => {
          Modal.error({
            title: "Update Failed",
            content: "An error occurred while updating the user.",
          });
        });
    }
  };

  const handleUserDeleteClick = (selectedUser: SimpleSecurityUserAdminDto) => {
    if (selectedUser.id != null) {
      Modal.confirm({
        title: "Are you sure you want to delete user?",
        icon: <DeleteOutlined className="modal-delete-icon" />,
        okText: "Delete",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await deleteSimpleSecurityUser({
              configurationId,
              simpleSecurityUserId: selectedUser.id!,
            });
          } catch (error) {
            console.error("Failed to delete user:", error);
          }
        },
      });
    }
  };

  return (
    <div>
      {isUsersLoading || isSimpleSecurityDefinitionLoading ? (
        <CustomSpinner />
      ) : (
        <div>
          <div className="heading">
            <h1 className="custom-page-heading">Users</h1>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddUserClick}
              data-testid="new-user-button"
              className="custom-button"
            >
              New User
            </Button>
          </div>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={handleEditDefinitionClick}
            data-testid="edit-access-definition-button"
            className="custom-button"
          >
            Edit Access Definition
          </Button>
          <UsersTable
            simpleSecurityDefinition={simpleSecurityDefinition}
            usersData={simpleSecurityUsersData}
            onRowClick={handleRowClick}
            deleteUser={handleUserDeleteClick}
          />
          {isUserEditModalVisible && (
            <UserEditModal
              onSave={handleUserUpdate}
              onClose={handleUserEditModalClose}
              selectedUser={selectedUser ?? undefined}
              simpleSecurityDefinition={simpleSecurityDefinition}
              data-testid="user-edit-modal"
            ></UserEditModal>
          )}

          {isDefinitionEditModalVisible && (
            <AccessDefinitionModal
              simpleSecurityDefinition={simpleSecurityDefinition}
              onSave={handleSimpleSecurityDefinitionUpdate}
              onClose={handleDefinitionEditModalClose}
            ></AccessDefinitionModal>
          )}
          <div className="back-button-wrapper">
            <Button
              onClick={() => navigate(`/configuration/${configurationId}`)}
              className="back-button"
              size="large"
            >
              Back
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
export default UsersComponent;
